import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
	API_ENDPOINTS,
	Button,
	customToast,
	FormError,
	FormInput,
	Modal,
	replaceKeyWithValue,
	route,
	StringHelpers,
	useDisabledContext,
	useLangContext,
	Variants,
} from 'carrier-fe';
import { AppliancePickerOption } from '../../../types/appliance-management';

type Props = {
	open: boolean;
	onClose: () => void;
	applianceId: string | null;
	applianceName: string | null;
	/** How many options the target currently has, so the warning can say what is being replaced. */
	currentWarrantyCount?: number;
	onCopied: () => void;
};

/**
 * CUS-936's "Use Warranty Options [X]".
 *
 * This is a replace, not a merge: the endpoint deletes the target's whole option set before copying, which
 * is what the ticket asks for ("so the new appliance perfectly mirrors the selected template") but is not
 * what "import" sounds like. So the modal says how many options are about to be removed, and the confirm
 * button is destructive.
 *
 * Sources with no warranty options are disabled rather than hidden. The API rejects them for the same
 * reason - copying nothing would leave the appliance with nothing - and a disabled row with a reason is
 * more use than an absence the user cannot explain.
 */
function ImportWarrantyModal({
	open,
	onClose,
	applianceId,
	applianceName,
	currentWarrantyCount = 0,
	onCopied,
}: Props) {
	const { crud, fields } = useLangContext();
	const { disabled, setDisabled } = useDisabledContext();

	const [appliances, setAppliances] = useState<AppliancePickerOption[]>([]);
	const [sourceId, setSourceId] = useState<string>('');
	const [errors, setErrors] = useState<Record<string, string[]>>({});
	const [isFetching, setIsFetching] = useState<boolean>(false);

	useEffect(() => {
		if (!open || !applianceId) return;

		setSourceId('');
		setErrors({});
		fetchAppliances();
	}, [open, applianceId]);

	const fetchAppliances = async () => {
		setIsFetching(true);

		try {
			const { data } = await axios.get(
				route(API_ENDPOINTS.GENERIC.SELECT.VIESSMANN_APPLIANCES),
				{ params: { exclude: applianceId } }
			);

			setAppliances(data?.data ?? []);
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'Error loading the appliance list.',
				variant: Variants.Danger,
			});
		} finally {
			setIsFetching(false);
		}
	};

	const options = useMemo(
		() =>
			appliances.map((item) => ({
				label:
					item.warranty_count > 0
						? `${item.label} (${item.warranty_count})`
						: item.label,
				value: item.value,
				disabled: item.warranty_count === 0,
				description:
					item.warranty_count === 0
						? fields?.no_warranty_options_to_copy ||
						  'No warranty options to copy'
						: undefined,
			})),
		[appliances, fields]
	);

	const selected = useMemo(
		() => appliances.find((item) => item.value === sourceId),
		[appliances, sourceId]
	);

	const copy = async () => {
		if (!applianceId || !sourceId) return;

		setDisabled(true);
		setErrors({});

		try {
			const { data } = await axios.post(
				route(API_ENDPOINTS.ADMIN.VIESSMANN_APPLIANCE.WARRANTY.COPY, {
					viessmannAppliance: applianceId,
				}),
				{ source_appliance_id: sourceId }
			);

			customToast({
				title: data?.message || 'Warranty options copied.',
				variant: Variants.Success,
			});

			onCopied();
			onClose();
		} catch (err: any) {
			setErrors(err?.response?.data?.data?.errors ?? {});

			customToast({
				title:
					err?.response?.data?.message ||
					'Error copying the warranty options.',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
		}
	};

	return (
		<Modal
			open={open}
			onClose={onClose}
			title={StringHelpers.title(
				replaceKeyWithValue(
					crud?.modals?.import_warranty?.title ||
						'Use warranty options from another appliance',
					'name',
					applianceName || ''
				)
			)}
			closeText={crud?.buttons?.cancel?.default || 'Cancel'}
			/*
			 * Only the in-flight state, so a request cannot be abandoned half way. The shared Modal hands
			 * this same prop to the header's X and to Cancel as well as to Confirm, so putting the "have
			 * they chosen a source yet" condition here would trap the user in a modal they had not filled
			 * in. The confirm button carries that condition instead, via overriddenConfirmButton, which the
			 * footer renders untouched by disabled.
			 */
			disabled={disabled}
			onConfirm={copy}
			overriddenConfirmButton={
				<Button
					label={
						disabled
							? crud?.buttons?.submit?.submitting || 'Copying...'
							: crud?.buttons?.submit?.default || 'Copy'
					}
					variant={Variants.Danger}
					onClick={copy}
					disabled={disabled || isFetching || !sourceId}
				/>
			}
			variant={Variants.Danger}
		>
			<p>
				{replaceKeyWithValue(
					'Copy the warranty options of another appliance onto :name.',
					'name',
					applianceName || 'this appliance'
				)}
			</p>

			{/*
			 * The whole point of the warning. copyWarrantiesFrom() empties the target first, so this is
			 * not additive, and an admin who reads "import" will not expect to lose anything.
			 */}
			{currentWarrantyCount > 0 && (
				<p className="fw-bold text-danger">
					{replaceKeyWithValue(
						fields?.import_warranty_replaces ||
							'This replaces every warranty option already set on this appliance. :count will be removed.',
						'count',
						`${currentWarrantyCount} ${
							currentWarrantyCount === 1
								? 'existing option'
								: 'existing options'
						}`
					)}
				</p>
			)}

			<FormInput
				type="searchable-select"
				name="source_appliance_id"
				/*
				 * Not title-cased: the lang value carries its own casing so the preposition stays lowercase,
				 * rather than reading "Appliance To Copy From".
				 */
				label={fields?.source_appliance_id || 'Appliance to Copy From'}
				placeholder={StringHelpers.title(
					fields?.please_select || 'please select'
				)}
				value={sourceId}
				onChange={(value) => setSourceId(value as string)}
				options={options}
				disabled={disabled || isFetching}
				errorMessages={errors?.source_appliance_id}
			/>

			{selected && (
				<p className="text-muted mb-0">
					{replaceKeyWithValue(
						fields?.import_warranty_summary ||
							':count option(s) will be copied from :source.',
						'count',
						String(selected.warranty_count)
					).replace(':source', selected.label)}
				</p>
			)}

			{!!errors?.message && <FormError errorMessages={errors.message} />}
		</Modal>
	);
}

export default ImportWarrantyModal;
